import { useMemo } from "react";
import { GitCompareArrows, ArrowRight } from "lucide-react";

type SnapFinding = {
  internal_id: string;
  scanner_name?: string;
  level?: string | null;
  name?: string | null;
};

export type ComparisonSnapshot = {
  id: string;
  source: string;
  created_at: string;
  finding_count: number;
  findings?: SnapFinding[] | null;
};

/**
 * Scan-to-scan comparison of the two most recent snapshots: findings that
 * appeared, findings that were resolved, and findings whose severity changed.
 */
export function SecurityScanComparison({ snapshots }: { snapshots: ComparisonSnapshot[] }) {
  const [latest, previous] = snapshots;

  const diff = useMemo(() => {
    const cur = new Map((latest?.findings ?? []).map((f) => [f.internal_id, f]));
    const prev = new Map((previous?.findings ?? []).map((f) => [f.internal_id, f]));
    const added = [...cur.values()].filter((f) => !prev.has(f.internal_id));
    const resolved = [...prev.values()].filter((f) => !cur.has(f.internal_id));
    const severity = [...cur.values()]
      .filter((f) => prev.has(f.internal_id))
      .map((f) => ({ finding: f, from: prev.get(f.internal_id)?.level ?? null, to: f.level ?? null }))
      .filter((r) => (r.from ?? "") !== (r.to ?? ""));
    return { added, resolved, severity };
  }, [latest, previous]);

  if (!latest || !previous) {
    return (
      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="font-display text-lg font-bold flex items-center gap-2">
          <GitCompareArrows className="size-5 text-primary" />
          Scan comparison
        </h2>
        <p className="text-sm text-muted-foreground mt-2">
          Two completed scans are needed before a comparison can be shown.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <GitCompareArrows className="size-5 text-primary" />
            What changed since the previous scan
          </h2>
          <p className="font-mono text-[10px] text-muted-foreground mt-1">
            {new Date(previous.created_at).toLocaleString()} ({previous.finding_count}) →{" "}
            {new Date(latest.created_at).toLocaleString()} ({latest.finding_count})
          </p>
        </div>
        <div className="flex gap-2 font-mono text-[10px] uppercase">
          <span className="rounded border border-destructive/40 text-destructive px-2 py-1">
            {diff.added.length} new
          </span>
          <span className="rounded border border-primary/40 text-primary px-2 py-1">
            {diff.resolved.length} resolved
          </span>
          <span className="rounded border border-border px-2 py-1 text-muted-foreground">
            {diff.severity.length} severity
          </span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Column title="New findings" tone="alert" empty="No new findings.">
          {diff.added.map((f) => (
            <Row key={f.internal_id} id={f.internal_id} name={f.name} level={f.level} />
          ))}
        </Column>
        <Column title="Resolved" tone="ok" empty="Nothing resolved in this run.">
          {diff.resolved.map((f) => (
            <Row key={f.internal_id} id={f.internal_id} name={f.name} level={f.level} />
          ))}
        </Column>
        <Column title="Severity changes" empty="No severity changes.">
          {diff.severity.map((r) => (
            <div
              key={r.finding.internal_id}
              className="rounded-md border border-border/60 bg-secondary/20 p-2"
            >
              <div className="font-mono text-[11px] break-all">{r.finding.internal_id}</div>
              <div className="font-mono text-[10px] uppercase text-muted-foreground flex items-center gap-1 mt-1">
                {r.from ?? "none"}
                <ArrowRight className="size-3" />
                <span className="text-foreground">{r.to ?? "none"}</span>
              </div>
            </div>
          ))}
        </Column>
      </div>
    </div>
  );
}

function Column({
  title,
  tone,
  empty,
  children,
}: {
  title: string;
  tone?: "alert" | "ok";
  empty: string;
  children: React.ReactNode;
}) {
  const hasItems = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <div>
      <h3
        className={`font-mono text-[10px] uppercase mb-2 ${
          tone === "alert" ? "text-destructive" : tone === "ok" ? "text-primary" : "text-muted-foreground"
        }`}
      >
        {title}
      </h3>
      {hasItems ? (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">{children}</div>
      ) : (
        <p className="text-xs text-muted-foreground">{empty}</p>
      )}
    </div>
  );
}

function Row({ id, name, level }: { id: string; name?: string | null; level?: string | null }) {
  return (
    <div className="rounded-md border border-border/60 bg-secondary/20 p-2">
      <div className="font-mono text-[11px] break-all">{id}</div>
      {name && <div className="text-xs text-muted-foreground mt-0.5">{name}</div>}
      {level && (
        <div className="font-mono text-[10px] uppercase text-muted-foreground mt-0.5">{level}</div>
      )}
    </div>
  );
}
